import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { optimizationPricesResultSelector } from '../../../../reducers/OtimizacaoPrecos';
import {
  applySharedFiltersToHistoryAndAnalysis,
  BigNumbers,
  PeriodStatus,
  setOptimizationResult,
  setOptimizationResultBigNumbers,
  setOptimizationResultChart,
  setSelectedStoresForecastTab,
} from '../../../../reducers/OtimizacaoPrecos/optimizationPricesResult';
import { snakeCaseToCamelCase } from '../../../../utils/snakeCaseToCamelCase';
import { getMargemObjetivaSegmento } from '../../Estrategia/pages/MargemObjetiva/services';
import { getOptimizationBigNumbers, getOptimizationChartData, getOptimizationResult } from '../services';

export function useResult() {
  const { id } = useParams() as Record<'id', string>;
  const dispatch = useDispatch();
  const { result, tabs } = useSelector(optimizationPricesResultSelector, shallowEqual);
  const { storesSelected } = tabs.forecast.filters;
  const { userAlreadyAppliedAnyFilter } = result;

  const { data: defaultCategory } = useQuery({
    queryKey: ['optimization-prices-default-category-level'],
    retry: true,
    keepPreviousData: true,
    enabled: !!id,
    queryFn: () => getMargemObjetivaSegmento(),
  });

  const { isLoading: isLoadingResult, data: optimizationResult } = useQuery({
    queryKey: ['optimization-prices-result', id],
    retry: true,
    keepPreviousData: true,
    enabled: !!id && !!defaultCategory,
    queryFn: () => getOptimizationResult(id),
    onSuccess(data) {
      const result = snakeCaseToCamelCase(data);

      if (!result) {
        return;
      }

      dispatch(
        setOptimizationResult({
          result: {
            ...result,
            category: {
              name: defaultCategory?.name,
              level: defaultCategory?.level,
              value: result.categories,
            },
            lastOptimization: result.lastOptimization ? new Date(`${result.lastOptimization}T00:00:00`) : null,
            nextOptimization: result.nextOptimization ? new Date(`${result.nextOptimization}T00:00:00`) : null,
            editedAt: result.editedAt ? new Date(`${result.editedAt}T00:00:00`) : null,
            editedBy: result.editedBy,
            createdAt: result.createdAt ? new Date(`${result.createdAt}T00:00:00`) : new Date(),
            period: {
              value: result.period,
              label: PeriodStatus[result.period as keyof typeof PeriodStatus],
            },
            controlStore: result.controlStore,
            stores: result.stores,
            storesIdWithoutResults: result.storesIdWithoutResults || [],
            totalProducts: result.totalProducts,
            userAlreadyAppliedAnyFilter: userAlreadyAppliedAnyFilter,
            status: result.status,
          },
          validity: {
            start: new Date(`${result.validity.startDate}T00:00:00`),
            end: result.validity.endDate ? new Date(`${result.validity.endDate}T00:00:00`) : undefined,
          },
        }),
      );

      const storesId = result?.stores?.map((store: { storeId: string }) => store.storeId) || [];
      const storesIdWithoutResults = storesId.filter((storeId: string) => !result.storesIdWithoutResults?.includes(storeId));

      const filtersAlreadyApplied = userAlreadyAppliedAnyFilter;

      if (!filtersAlreadyApplied) {
        const end = result.validity.endDate ? new Date(`${result.validity.endDate}T00:00:00`) : new Date();
        const start = result.createdAt ? new Date(`${result.createdAt}T00:00:00`) : new Date(`${result.validity.startDate}T00:00:00`);
        dispatch(
          applySharedFiltersToHistoryAndAnalysis({
            periodSelected: [start, end],
            analysisStoresSelected: storesIdWithoutResults,
            historyStoresSelected: storesIdWithoutResults,
          }),
        );
      }
    },
  });

  const storesId = useMemo(() => optimizationResult?.stores?.map((store: any) => store.store_id), [optimizationResult]);
  const canGetOthersApi = !!id && storesId?.length > 0 && !!optimizationResult?.last_optimization && storesSelected?.length > 0;
  const storesIdSelected = storesSelected?.length > 0 ? storesSelected : storesId;

  const { isLoading: isLoadingResultBigNumbers } = useQuery({
    queryKey: ['optimization-prices-forecast-big-numbers', id, storesIdSelected],
    retry: true,
    keepPreviousData: true,
    enabled: canGetOthersApi,
    queryFn: () => getOptimizationBigNumbers({ id, stores_id: storesIdSelected }),
    onSuccess(data) {
      const result = snakeCaseToCamelCase(data);

      const bigNumbersData: BigNumbers = {
        ...result,
        startDate: format(new Date(result.startDate), 'dd/MM/yyyy'),
        endDate: result.endDate ? format(new Date(result.endDate), 'dd/MM/yyyy') : null,
      };

      dispatch(
        setOptimizationResultBigNumbers({
          data: bigNumbersData,
        }),
      );
    },
  });

  const { isLoading: isLoadingResultChartData, data: chartData } = useQuery({
    queryKey: ['optimization-prices-forecast-chart-data', id, storesIdSelected],
    retry: true,
    keepPreviousData: true,
    enabled: canGetOthersApi,
    queryFn: () => getOptimizationChartData({ id, stores_id: storesIdSelected }),
    onSuccess(data) {
      const result = snakeCaseToCamelCase(data);
      dispatch(setOptimizationResultChart({ data: result }));
    },
  });

  const handleSelectStoreId = useCallback(
    (storesIdSelected: string[]) => {
      dispatch(setSelectedStoresForecastTab({ id: storesIdSelected }));
    },
    [dispatch],
  );

  return {
    isLoadingResult,
    isLoadingResultBigNumbers,
    isLoadingResultChartData,
    hasResult: !!chartData?.optimized,
    handleSelectStoreId,
    stores: result.stores,
    storesSelected,
    hasOptimization: !!result.lastOptimization,
    nextOptimization: result.nextOptimization,
  };
}
